import { Box, Button, FormLabel, useToast } from '@chakra-ui/react';
import { PeOrderOperationType } from 'enums/modules/pe/peOperationType';
import { Form, Formik } from 'formik';
import useFormError from 'hooks/shared/useFormError';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import FormNumberInput from 'presentation/components/shared/form/FormNumberInput';
import AppCard from 'presentation/components/shared/card/AppCard';
import ButtonTabs from 'presentation/components/shared/tabs/ButtonTabs';
import { useCreatePeOrderMutation } from 'rtkQuery/sharespro/queries/peService';
import FormTextInput from 'presentation/components/shared/form/FormTextInput';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import { useNavigate } from 'react-router-dom';
import AppRoute from 'router/AppRoute';

interface FormValues {
  operation: PeOrderOperationType;
  price: string;
  name: string;
  sum: string;
  cnt: string;
  comment: string;
}

const CreatePeOrderForm: FC = () => {
  const formErrorHandler = useFormError<FormValues>();

  const { t } = useTranslation();

  const toast = useToast();

  const [createPeOrder, { isLoading }] = useCreatePeOrderMutation();

  const navigate = useNavigate();

  const operationTypes = [PeOrderOperationType.Sell, PeOrderOperationType.Buy];

  const operationText = {
    [PeOrderOperationType.Buy]: t('buy'),
    [PeOrderOperationType.Sell]: t('sell'),
  };

  return (
    <>
      <GridHeading>{t('peOrder')}</GridHeading>
      <AppCard p="40px">
        <Formik<FormValues>
          initialValues={{
            operation: PeOrderOperationType.Buy,
            price: '',
            name: '',
            sum: '',
            cnt: '',
            comment: '',
          }}
          onSubmit={(values, formik) => {
            const requestData: ApiCreatePeOrderRequestData = {
              operation: values.operation,
            };
            if (values.price.trim()) {
              requestData.price = +values.price;
            }
            if (values.sum.trim()) {
              requestData.sum = +values.sum;
            }
            if (values.name.trim()) {
              requestData.name = values.name;
            }
            if (values.cnt.trim()) {
              requestData.cnt = +values.cnt;
            }
            if (values.comment.trim()) {
              requestData.comment = values.comment;
            }
            createPeOrder(requestData)
              .unwrap()
              .then(() => {
                toast({
                  title: t('peYourRequestWasSent'),
                  description: t('peManagerWillContactYou'),
                  status: 'success',
                  duration: 10 * 1000,
                  position: 'top-right',
                  isClosable: true,
                });
                navigate(AppRoute.peOffers);
              })
              .catch((err) => formErrorHandler(err, formik));
          }}
        >
          {({ values, handleChange, setFieldValue }) => {
            return (
              <Form>
                <Box mb="40px">
                  <FormLabel>{t('peCreateOrderOperationLabel')}</FormLabel>
                  <ButtonTabs
                    onChange={(index) =>
                      setFieldValue('operation', operationTypes[index])
                    }
                    index={operationTypes.indexOf(values.operation)}
                    items={operationTypes.map(
                      (period) => operationText[period]
                    )}
                    tabItemProps={{
                      flex: 1,
                    }}
                  />
                </Box>
                <Box mb="40px">
                  <FormTextInput
                    name="name"
                    onChange={handleChange}
                    value={values.name}
                    label={t('peCreateOrderCompanyName')}
                  />
                </Box>
                {values.operation === PeOrderOperationType.Sell && (
                  <Box mb="40px">
                    <FormNumberInput
                      name="cnt"
                      onChange={(val) => setFieldValue('cnt', val)}
                      value={values.cnt}
                      label={t('peCreateOrderQuantity')}
                    />
                  </Box>
                )}
                <Box mb="40px">
                  <FormNumberInput
                    name="sum"
                    value={values.sum}
                    onChange={(val) => setFieldValue('sum', val)}
                    label={t('peCreateOrderAmount')}
                  />
                </Box>
                {values.operation === PeOrderOperationType.Sell && (
                  <Box mb="40px">
                    <FormNumberInput
                      name="price"
                      onChange={(val) => setFieldValue('price', val)}
                      value={values.price}
                      label={t('peCreateOrderSharePrice')}
                    />
                  </Box>
                )}
                <Box mb="40px">
                  <FormTextInput
                    name="comment"
                    onChange={handleChange}
                    value={values.comment}
                    label={t('peCreateOrderComment')}
                  />
                </Box>

                <Button type="submit" isLoading={isLoading}>
                  {t('peMakeRequest')}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </AppCard>
    </>
  );
};

export default CreatePeOrderForm;
